import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject} from '@angular/core';
import {take} from 'rxjs/operators';
import {Browser} from '../../types/browser';
import {Device} from '../../types/devices/device';
import {DevicePropertyNames} from '../../types/devices/device-properties';
import {ExpeditionId} from '../../types/ids';
import {UserInfo} from '../../types/user/user-info';
import {BrowserSessionInfo} from '../../types/browsersession';
import {BrowserSessionService} from '../../services/browsersession.service';
import {TOOL_CONTAINER_DATA} from '../../tools/modaltoolcontainer/modaltoolcontainer.component';
import {ModalToolContainerService, WebmateToolConfig} from '../../tools/modaltoolcontainer/modaltoolcontainer.service';
import {
    BrowsersessionViewerComponent,
    BrowsersessionViewerToolConfig
} from '../../tools/browsersessionviewer/browsersessionviewer.component';

interface DevicePropertyDetail {
    fullKey: string;
    key: string;
    value: unknown;
}

interface DevicePropertyGroup {
    name: string;
    category: DeviceDetailCategory;
    entries: DevicePropertyDetail[];
}

type DeviceDetailCategory = 'primary' | 'other';

interface DeviceDetailSection {
    id: DeviceDetailCategory;
    title: string;
    groups: DevicePropertyGroup[];
}

export class DeviceDetailsToolConfig implements WebmateToolConfig {
    toolName = 'Device Details';
    scrollable = true;

    constructor(public device: Device,
                public browsers: Browser[],
                public deviceCreator?: UserInfo,
                public activeBrowserSession?: BrowserSessionInfo,
                public oldBrowserSessions: BrowserSessionInfo[] = []) {}
}

@Component({
    selector: 'wm-device-details',
    templateUrl: './device-details.component.html',
    styleUrls: ['./device-details.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceDetailsComponent {
    private static readonly PRIMARY_GROUP_ORDER = ['machine', 'streaming', 'webmate'];

    protected readonly Array = Array;
    protected readonly DevicePropertyNames = DevicePropertyNames;
    protected readonly detailSections: DeviceDetailSection[];
    protected readonly expandedSections = new Set<DeviceDetailCategory>(['primary']);
    protected readonly expandedGroups = new Set<string>();
    protected readonly browserSessionsGroupId = 'other-browser-sessions';

    constructor(@Inject(TOOL_CONTAINER_DATA) protected readonly config: DeviceDetailsToolConfig,
                private readonly browserSessionService: BrowserSessionService,
                private readonly modalToolService: ModalToolContainerService,
                private readonly cdRef: ChangeDetectorRef) {
        const groups = this.getGroupedDeviceProperties();
        const primaryGroups = groups.filter(group => group.category === 'primary');
        const otherGroups = groups.filter(group => group.category === 'other');
        primaryGroups.forEach(group => this.expandedGroups.add(this.groupId(group)));
        this.detailSections = [
            {id: 'primary', title: 'Primary Details', groups: primaryGroups},
            {id: 'other', title: 'Other Details', groups: otherGroups}
        ];
    }

    getGroupedDeviceProperties(): DevicePropertyGroup[] {
        const groups = new Map<string, DevicePropertyGroup>();
        Object.keys(this.config.device.properties).sort().forEach(fullKey => {
            const separatorIndex = fullKey.indexOf('.');
            const groupName = separatorIndex > 0 ? fullKey.substring(0, separatorIndex) : 'General';
            const key = separatorIndex > 0 ? fullKey.substring(separatorIndex + 1) : fullKey;
            let group = groups.get(groupName);
            if (group === undefined) {
                group = {
                    name: groupName,
                    category: DeviceDetailsComponent.PRIMARY_GROUP_ORDER.includes(groupName) ? 'primary' : 'other',
                    entries: []
                };
                groups.set(groupName, group);
            }
            group.entries.push({fullKey, key, value: this.config.device.properties[fullKey]});
        });
        return Array.from(groups.values()).sort((a, b) => {
            if (a.category !== b.category) {
                return a.category === 'primary' ? -1 : 1;
            }
            if (a.category === 'primary') {
                return DeviceDetailsComponent.PRIMARY_GROUP_ORDER.indexOf(a.name) - DeviceDetailsComponent.PRIMARY_GROUP_ORDER.indexOf(b.name);
            }
            return a.name.localeCompare(b.name);
        });
    }

    protected hasSectionContent(section: DeviceDetailSection): boolean {
        return section.groups.length > 0 || (section.id === 'other' && this.hasBrowserSessions);
    }

    protected get hasBrowserSessions(): boolean {
        return this.config.activeBrowserSession !== undefined || this.config.oldBrowserSessions.length > 0;
    }

    protected toggleSection(sectionId: DeviceDetailCategory): void {
        this.toggleSetValue(this.expandedSections, sectionId);
    }

    protected toggleGroup(group: DevicePropertyGroup): void {
        this.toggleSetValue(this.expandedGroups, this.groupId(group));
    }

    protected toggleGroupId(groupId: string): void {
        this.toggleSetValue(this.expandedGroups, groupId);
    }

    protected groupId(group: DevicePropertyGroup): string {
        return `${group.category}-${group.name}`;
    }

    protected sectionContentId(section: DeviceDetailSection): string {
        return `device-details-section-${section.id}`;
    }

    protected groupContentId(group: DevicePropertyGroup): string {
        return `device-details-group-${this.groupId(group)}`;
    }

    private toggleSetValue<T>(values: Set<T>, value: T): void {
        if (values.has(value)) {
            values.delete(value);
        } else {
            values.add(value);
        }
    }

    prettyPrintDetailValue(value: unknown): string {
        switch (typeof value) {
            case 'number':
            case 'string':
            case 'boolean':
                return `${value}`;
            default:
                return JSON.stringify(value);
        }
    }

    isObjectValue(value: unknown): boolean {
        return value !== null && typeof value === 'object';
    }

    stopExpedition(expeditionId: ExpeditionId): void {
        this.browserSessionService.stopExpedition$(expeditionId).pipe(take(1)).subscribe(() => {
            this.config.activeBrowserSession = undefined;
            this.cdRef.markForCheck();
        });
    }

    openBrowserSessionViewer(sessionId: ExpeditionId): void {
        this.modalToolService.openTool(BrowsersessionViewerComponent, new BrowsersessionViewerToolConfig(sessionId));
    }
}
